import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useSearchParams } from 'react-router-dom';

interface Meeting {
  id: number;
  title: string;
  start_time: string;
  description: string;
  minutes: string;
  responsible_name: string;
}

interface Attendance {
  person_name: string;
  person_type: string;
  checkin_method: string;
  checked_in_at: string;
}

interface Settings {
  school_name: string;
  logo_url: string;
  primary_color: string;
}

const MeetingReport: React.FC = () => {
  const [searchParams] = useSearchParams();
  const meetingId = searchParams.get('id');
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [attendees, setAttendees] = useState<Attendance[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [meetingRes, attendanceRes, settingsRes] = await Promise.all([
          api.get(`/meetings/${meetingId}/`),
          api.get(`/meetings/${meetingId}/attendance/`),
          api.get('/settings/')
        ]);
        setMeeting(meetingRes.data);
        setAttendees(attendanceRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error("Error fetching report data", error);
      }
    };
    if (meetingId) fetchData();
  }, [meetingId]);

  if (!meeting) return <div>Carregando relatório...</div>;

  return (
    <div className="report-container">
      <style>{`
        @media print {
          @page { margin: 10mm; size: A4; }
          body { background: white; -webkit-print-color-adjust: exact; }
          .no-print { display: none !important; }
          .report-container { padding: 0; box-shadow: none; margin: 0; width: 100%; max-width: none; }
          /* Hide browser headers/footers */
          header, footer { display: none; }
        }
        .report-container {
          max-width: 210mm;
          margin: 20px auto;
          background: white;
          padding: 40px;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
          font-family: 'Times New Roman', serif;
          color: #000;
        }
        .header {
          display: flex;
          align-items: center;
          border-bottom: 2px solid ${settings?.primary_color || '#000'};
          padding-bottom: 20px;
          margin-bottom: 30px;
        }
        .logo {
          max-height: 80px;
          margin-right: 20px;
        }
        .title {
          flex: 1;
          text-align: center;
        }
        .title h1 { margin: 0; font-size: 1.5rem; text-transform: uppercase; }
        .title h2 { margin: 5px 0 0; font-size: 1.2rem; font-weight: normal; }
        .section { margin-bottom: 25px; page-break-inside: avoid; }
        .section-title {
          font-size: 1.1rem;
          font-weight: bold;
          background-color: #f0f0f0;
          padding: 5px 10px;
          margin-bottom: 10px;
          border-left: 4px solid ${settings?.primary_color || '#000'};
        }
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        .minutes-content {
          white-space: pre-wrap;
          text-align: justify;
          line-height: 1.5;
        }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 0.9rem; }
        th { background-color: #f9f9f9; }
      `}</style>

      <div className="no-print" style={{ marginBottom: '20px', textAlign: 'right' }}>
        <button className="btn" onClick={() => window.print()}>🖨️ Imprimir / Salvar PDF</button>
      </div>

      <div className="header">
        {settings?.logo_url && <img src={settings.logo_url} alt="Logo" className="logo" />}
        <div className="title">
          <h1>{settings?.school_name || 'Escola'}</h1>
          <h2>Relatório de Reunião</h2>
        </div>
      </div>

      <div className="section">
        <div className="section-title">Dados Gerais</div>
        <div className="info-grid">
          <div><strong>Título:</strong> {meeting.title}</div>
          <div><strong>Data:</strong> {new Date(meeting.start_time).toLocaleDateString()}</div>
          <div><strong>Responsável:</strong> {meeting.responsible_name}</div>
          <div><strong>Horário:</strong> {new Date(meeting.start_time).toLocaleTimeString()}</div>
        </div>
      </div>

      <div className="section">
        <div className="section-title">Pauta / Descrição</div>
        <p>{meeting.description || 'Sem descrição.'}</p>
      </div>

      <div className="section">
        <div className="section-title">Ata da Reunião</div>
        <div className="minutes-content">
          {meeting.minutes || 'Nenhuma ata registrada.'}
        </div>
      </div>

      <div className="section">
        <div className="section-title">Lista de Presença ({attendees.length})</div>
        <table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Check-in</th>
            </tr>
          </thead>
          <tbody>
            {attendees.map((att, index) => (
              <tr key={index}>
                <td>{att.person_name}</td>
                <td>{att.person_type === 'Parent' ? 'Responsável' : att.person_type === 'Staff' ? 'Colaborador' : 'Convidado'}</td>
                <td>{att.checkin_method === 'QR' ? 'Digital' : 'Manual'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '50px', display: 'flex', justifyContent: 'space-between', pageBreakInside: 'avoid' }}>
        <div style={{ textAlign: 'center', width: '40%' }}>
          <div style={{ borderTop: '1px solid #000', paddingTop: '5px' }}>Responsável</div>
        </div>
        <div style={{ textAlign: 'center', width: '40%' }}>
          <div style={{ borderTop: '1px solid #000', paddingTop: '5px' }}>Direção</div>
        </div>
      </div>
    </div>
  );
};

export default MeetingReport;
